import React from 'react'
import { Popover } from '@base-ui-components/react/popover'
import { Checkbox } from '@base-ui-components/react/checkbox'
import { Table } from '@tanstack/react-table'
import {
  Sortable,
  SortableItem,
  SortableItemHandle,
} from '../../../../../components/ui/Sortable'
import { LogEntry } from '../types'
import useTranslate from '../useTranslate'
import useStyles from '../styles'

interface ColumnSettingsButtonProps {
  table: Table<LogEntry>
  // Columns whose visibility is forced elsewhere (e.g. Level while a level
  // filter is active) — their toggle is disabled so it doesn't look broken.
  disabledColumnIds?: string[]
}

const ColumnSettingsButton: React.FC<ColumnSettingsButtonProps> = ({
  table,
  disabledColumnIds = [],
}) => {
  const classes = useStyles()
  const t = useTranslate()

  const columns = table.getAllLeafColumns()

  return (
    <Popover.Root>
      <Popover.Trigger className={classes.exportButton}>
        <span
          className='material-symbols-outlined'
          style={{ fontSize: 16 }}
        >
          view_column
        </span>
        <span>{t('log_viewer.columns')}</span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          className={classes.comboboxPositioner}
          align='end'
          sideOffset={4}
        >
          <Popover.Popup className={classes.columnSettingsPopup}>
            <div className={classes.columnSettingsHeader}>
              <button
                type='button'
                className={classes.columnSettingsLink}
                onClick={() => table.toggleAllColumnsVisible(true)}
              >
                Show All
              </button>
              <button
                type='button'
                className={classes.columnSettingsLink}
                onClick={() => table.toggleAllColumnsVisible(false)}
              >
                Hide All
              </button>
              <button
                type='button'
                className={`${classes.columnSettingsLink} ${classes.columnSettingsReset}`}
                onClick={() => {
                  table.setColumnOrder([])
                  table.toggleAllColumnsVisible(true)
                }}
              >
                Reset
              </button>
            </div>
            <Sortable
              value={columns}
              onValueChange={next =>
                table.setColumnOrder(next.map(column => column.id))
              }
              getItemValue={column => column.id}
              className={classes.columnSettingsList}
            >
              {columns.map(column => (
                <SortableItem
                  key={column.id}
                  value={column.id}
                  className={classes.columnSettingsRow}
                >
                  <Checkbox.Root
                    className={classes.columnSettingsToggle}
                    checked={column.getIsVisible()}
                    onCheckedChange={() => column.toggleVisibility()}
                    disabled={disabledColumnIds.includes(column.id)}
                    title={
                      disabledColumnIds.includes(column.id)
                        ? 'Hidden while a level filter is active'
                        : undefined
                    }
                  >
                    <span className={classes.columnSettingsBox}>
                      <Checkbox.Indicator
                        className={classes.columnSettingsIndicator}
                      >
                        <span
                          className='material-symbols-outlined'
                          style={{ fontSize: 13 }}
                        >
                          check
                        </span>
                      </Checkbox.Indicator>
                    </span>
                    <span className={classes.columnSettingsLabel}>
                      {typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                    </span>
                  </Checkbox.Root>
                  <SortableItemHandle
                    className={classes.columnSettingsHandle}
                    aria-label={`Reorder ${column.id} column`}
                  >
                    <span
                      className='material-symbols-outlined'
                      style={{ fontSize: 16 }}
                    >
                      drag_indicator
                    </span>
                  </SortableItemHandle>
                </SortableItem>
              ))}
            </Sortable>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

export default ColumnSettingsButton
